import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logoIcon from "@/assets/logo_icon.png";

/**
 * Full-screen animated preloader shown while the app boots.
 * Blurred backdrop lets the page skeleton/elements shimmer through while
 * they load; progress tracks the real `window.load` event (capped at 90%
 * until it fires), then fills to 100% and fades out.
 */
export const AppPreloader = () => {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const loadFired = useRef(document.readyState === "complete");

  useEffect(() => {
    const onLoad = () => {
      loadFired.current = true;
    };
    window.addEventListener("load", onLoad);
    // Safety net: never hold the preloader longer than 4s.
    const hardTimeout = setTimeout(() => {
      loadFired.current = true;
    }, 4000);

    const tick = setInterval(() => {
      setProgress((p) => {
        const target = loadFired.current ? 100 : 90;
        if (p >= target) return p;
        const step = Math.max(1, Math.round((target - p) * 0.14));
        return Math.min(target, p + step);
      });
    }, 80);

    return () => {
      window.removeEventListener("load", onLoad);
      clearTimeout(hardTimeout);
      clearInterval(tick);
    };
  }, []);

  useEffect(() => {
    if (progress >= 100) {
      const t = setTimeout(() => setDone(true), 450);
      return () => clearTimeout(t);
    }
  }, [progress]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="app-preloader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.15 } }}
          exit={{ opacity: 0, transition: { duration: 0.55, ease: "easeOut" } }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/55 backdrop-blur-2xl"
          aria-label="Wird geladen"
        >
          {/* soft brand glow behind the logo */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(420px 420px at 50% 46%, hsl(var(--primary) / 0.16), transparent 70%)",
            }}
          />

          <div className="relative flex flex-col items-center gap-7">
            <div className="relative flex items-center justify-center">
              {/* rotating dashed ring */}
              <motion.svg
                viewBox="0 0 120 120"
                className="absolute h-32 w-32 text-primary/70"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2.4, ease: "linear" }}
              >
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray="70 260"
                />
              </motion.svg>

              {/* pulsing logo with shine sweep */}
              <motion.div
                className="relative overflow-hidden rounded-3xl"
                animate={{ scale: [1, 1.07, 1] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
              >
                <motion.img
                  src={logoIcon}
                  alt="Kaufyard"
                  className="h-20 w-20 select-none"
                  initial={{ opacity: 0, scale: 0.75 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  draggable={false}
                />
                <motion.div
                  className="pointer-events-none absolute inset-y-0 w-1/2"
                  style={{
                    background:
                      "linear-gradient(105deg, transparent 0%, hsl(0 0% 100% / 0.55) 50%, transparent 100%)",
                  }}
                  animate={{ x: ["-120%", "260%"] }}
                  transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut", repeatDelay: 0.4 }}
                />
              </motion.div>
            </div>

            {/* progress bar */}
            <div className="flex flex-col items-center gap-2">
              <div className="h-1 w-44 overflow-hidden rounded-full bg-muted">
                <motion.div
                  className="h-full rounded-full bg-primary"
                  animate={{ width: `${progress}%` }}
                  transition={{ ease: "easeOut", duration: 0.18 }}
                />
              </div>
              <span className="text-xs font-medium tabular-nums text-muted-foreground">
                {progress}%
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AppPreloader;
